/** @jsxImportSource @emotion/react */
"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import { css, type Interpolation } from "@emotion/react";
import { type Theme } from "../../styles";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const popoverContentStyles = css`
  z-index: 9999 !important;
  width: 18rem;
  border-radius: 0.375rem;
  border: 1px solid var(--color-border);
  background-color: var(--color-popover);
  color: var(--color-popover-foreground);
  padding: 1rem;
  box-shadow: var(--shadow-md);
  outline: none;
  isolation: isolate;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes zoomIn {
    from { transform: scale(0.95); }
    to { transform: scale(1); }
  }
  @keyframes zoomOut {
    from { transform: scale(1); }
    to { transform: scale(0.95); }
  }

  &[data-state="open"] {
    animation:
      fadeIn 0.15s ease-out,
      zoomIn 0.15s ease-out;
  }

  &[data-state="closed"] {
    animation:
      fadeOut 0.15s ease-in,
      zoomOut 0.15s ease-in;
  }

  &[data-state="open"][data-side="bottom"] {
    animation-name: slideInFromTop;
  }

  &[data-state="open"][data-side="left"] {
    animation-name: slideInFromRight;
  }

  &[data-state="open"][data-side="right"] {
    animation-name: slideInFromLeft;
  }

  &[data-state="open"][data-side="top"] {
    animation-name: slideInFromBottom;
  }
`;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & { css?: Interpolation<Theme> }
>(({ className, align = "center", sideOffset = 4, css: cssProp, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      css={[popoverContentStyles, cssProp]}
      className={className}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverContent, PopoverTrigger };
